import { t } from '@trimble/i18n';
import { ViewsCapabilityProvider } from '@trimble/web3d-providers';
import {
  Accessor,
  JSX,
  Show,
  Switch,
  Match,
  createSignal,
  createMemo,
  createEffect,
  on,
  untrack,
} from 'solid-js';
import { CustomInput, Spinner, Alert } from '@tcweb/connect-ui';
import { UnreachableCaseError } from 'ts-essentials';
import { UvIconButton } from '../../../button/UvIconButton';
import { ViewCardItem, ViewCards } from './ViewCards';
import { DropdownSelect } from '../../../drop-down/DropdownSelect';
import { PanelLayoutWithHeaderToggle } from '../../../panel-layout-with-header-toggle/PanelLayoutWithHeaderToggle';
import {
  SortableKeyOf,
  SortDirection,
  sortItems,
} from '../../../sort-drop-down-select/sort-utils';
import {
  SortDropDownOption,
  SortDropDownSelect,
} from '../../../sort-drop-down-select/SortDropDownSelect';
import {
  ViewDetailsResponse,
  TcTcpsApiService,
  TcRegion,
} from '@data-access/connect/tc-tcps';
import { useLogger } from '@trimble/solidjs-utils';
import { asError } from '@trimble/logging';
type SortByKey = SortableKeyOf<ViewDetailsResponse>;
const sortOptions: SortDropDownOption<SortByKey>[] = [
  {
    sortBy: 'name',
    text: t('mobileMapping.views.sortOptions.name'),
  },
  {
    sortBy: 'createdOn',
    text: t('mobileMapping.views.sortOptions.dateCreated'),
  },
  {
    sortBy: 'modifiedOn',
    text: t('mobileMapping.views.sortOptions.dateModified'),
  },
];

type FilterMode =
  | 'createdByMe'
  | 'sharedWithMe'
  | 'allViewsInProject'
  | 'inTheseModels';

const filterDropdownOptions: { key: FilterMode; label: string }[] = [
  {
    key: 'createdByMe',
    label: t('mobileMapping.views.filterOptions.createdByMe'),
  },
  {
    key: 'sharedWithMe',
    label: t('mobileMapping.views.filterOptions.sharedWithMe'),
  },
  {
    key: 'allViewsInProject',
    label: t('mobileMapping.views.filterOptions.allViewsInProject'),
  },
  {
    key: 'inTheseModels',
    label: t('mobileMapping.views.filterOptions.inTheseModels'),
  },
];

export interface ViewsTabProps {
  viewsProvider?: ViewsCapabilityProvider;
  tcTcpsApi: TcTcpsApiService;
  region: Accessor<TcRegion | undefined>;
  projectId: Accessor<string | undefined>;
  userEmail?: Accessor<string | undefined>;
  getVisibleModelIds?(): string[];
  arePrerequisitesMet?: Accessor<boolean>;
  isLoading?: Accessor<boolean>;
  hasErrors?: Accessor<boolean>;
  deletePointCloudAssociationsForView?(
    projectId: string,
    viewId: string
  ): Promise<void>;
  deleteReservedViewData?(viewId: string): Promise<void>;
  defaultSortBy?: SortByKey;
  defaultSortDirection?: SortDirection;
  onCardClick?(view: ViewDetailsResponse): void;
  onViewDetailsSelect?(view: ViewDetailsResponse): void;
  onCreateViewClick?(): void;
  onViewDeleted?(viewId: string): void;
  onViewDeleteError?(viewId: string, error: Error): void;
  onLoadOriginalVersions?(view: ViewDetailsResponse): void;
  onUpdateView?(view: ViewDetailsResponse): void;
}

export const ViewsTab = (props: ViewsTabProps): JSX.Element => {
  const logger = useLogger().getLogger('ViewsTab');

  const [sortBy, setSortBy] = createSignal<SortByKey>(
    props.defaultSortBy ?? 'name'
  );
  const [sortDirection, setSortDirection] = createSignal<SortDirection>(
    props.defaultSortDirection ?? 'asc'
  );
  const [filterText, setFilterText] = createSignal<string>('');
  const [filterMode, setFilterMode] =
    createSignal<FilterMode>('allViewsInProject');
  const [showSearch, setShowSearch] = createSignal<boolean>(false);
  // Keyed by view id; `thumbnail` changes (via its `modified` query param)
  // when the view is updated, which triggers a refetch of the blob.
  const [imageBlobUrlMap, setImageBlobUrlMap] = createSignal<
    Record<string, { readonly thumbnail: string; readonly blobUrl: string }>
  >({});

  // Internal views state used when no external viewsProvider is supplied
  const [internalViews, setInternalViews] = createSignal<ViewDetailsResponse[]>(
    []
  );

  const viewsQuery = createMemo(() => {
    const currentRegion = props.region();
    const currentProjectId = props.projectId();
    if (currentRegion === undefined || currentProjectId === undefined) {
      return undefined;
    }
    return props.tcTcpsApi.listViewsQuery(currentRegion, currentProjectId);
  });

  const normalizedFilter = createMemo(() => {
    return filterText().trim().toLowerCase();
  });

  const viewItems = (): ViewDetailsResponse[] =>
    props.viewsProvider ? props.viewsProvider.items() : internalViews();

  const views = createMemo(() => {
    const items = viewItems();
    const map = imageBlobUrlMap();
    const filter = normalizedFilter();
    const sortKey = sortBy();
    const sortDir = sortDirection();
    const mode = filterMode();
    const email = props.userEmail?.();
    const visibleModelIds = props.getVisibleModelIds?.() ?? [];

    return items
      .filter((view) => {
        if (!view.name.toLowerCase().includes(filter)) return false;

        switch (mode) {
          case 'createdByMe':
            return email !== undefined && view.createdBy.email === email;
          case 'sharedWithMe':
            return email !== undefined && view.createdBy.email !== email;
          case 'inTheseModels':
            return view.models.some((modelId) =>
              visibleModelIds.includes(modelId)
            );
          case 'allViewsInProject':
            return true;
          default:
            throw new UnreachableCaseError(mode);
        }
      })
      .toSorted((a, b) => sortItems(a, b, sortKey, sortDir))
      .map((view) => {
        return {
          ...view,
          title: view.name,
          imageUrl: map[view.id]?.blobUrl,
        } satisfies ViewCardItem;
      });
  });

  const handleSortChanged = (
    newSortBy: SortByKey,
    newSortDirection: SortDirection
  ): void => {
    setSortBy(newSortBy);
    setSortDirection(newSortDirection);
  };

  createEffect(() => {
    const query = viewsQuery();
    if (query?.isSuccess) {
      if (props.viewsProvider) {
        props.viewsProvider.updateViews(query.data);
      } else {
        setInternalViews(query.data);
      }
    }
  });

  createEffect(
    on(viewItems, (items) => {
      const map = imageBlobUrlMap();
      items.forEach((view) => {
        const thumbnail = view.thumbnail;
        if (map[view.id]?.thumbnail === thumbnail) return;

        props.tcTcpsApi
          .getImageBlobUrl(thumbnail)
          .then((blobUrl) => {
            // Drop a late result when the thumbnail changed while fetching.
            if (
              untrack(viewItems).find((v) => v.id === view.id)?.thumbnail !==
              thumbnail
            ) {
              return;
            }
            setImageBlobUrlMap((prev) => ({
              ...prev,
              [view.id]: { thumbnail, blobUrl },
            }));
          })
          .catch((err: unknown) => {
            logger.error(
              'Failed to load thumbnail for view',
              view,
              asError(err)
            );
          });
      });
    })
  );

  // If props provides prerequisite check, use it; otherwise default to basic check.
  const arePrerequisitesMet = createMemo(() => {
    const query = viewsQuery();
    if (props.arePrerequisitesMet) {
      return props.arePrerequisitesMet() && !!query?.isSuccess;
    }
    return query?.isSuccess && !!props.region() && !!props.projectId();
  });

  // Always include query error state so a failed views request is surfaced
  // even when an external error prop only covers data-state errors.
  const hasErrors = createMemo(() => {
    const query = viewsQuery();
    const queryError = query?.isError ?? false;
    if (props.hasErrors) {
      return props.hasErrors() || queryError;
    }
    return queryError;
  });

  // Derive loading state. When external loading/prerequisites props are provided,
  // also keep loading while the views query is still in flight.
  const isLoading = createMemo(() => {
    if (hasErrors()) {
      return false;
    }

    if (!props.projectId()) {
      return false;
    }

    if (arePrerequisitesMet()) {
      return false;
    }

    if (props.isLoading?.()) {
      return true;
    }

    // External prerequisites met but views query still in flight
    if (props.arePrerequisitesMet) {
      const query = viewsQuery();
      return !!query && !query.isSuccess && !query.isError;
    }

    return true;
  });

  function clearAndCloseSearch(): void {
    setFilterText('');
    setShowSearch(false);
  }

  const currentRegion = createMemo((): TcRegion | undefined => props.region());
  const currentProjectId = createMemo((): string | undefined =>
    props.projectId()
  );

  const handleViewDetailsSelect = (view: ViewDetailsResponse): void => {
    props.onViewDetailsSelect?.(view);
  };

  return (
    <PanelLayoutWithHeaderToggle
      panelTitle={t('mobileMapping.views.title')}
      titleSectionRight={
        <div class="contents" data-testid="views-filter-dropdown">
          <DropdownSelect
            options={filterDropdownOptions}
            value={filterMode()}
            onValueChange={(value) => value && setFilterMode(value)}
          />
        </div>
      }
      rightActions={
        <>
          <UvIconButton
            dataTestId="create-view-button"
            type="action"
            tooltip={t('mobileMapping.views.create.title')}
            icon="tc-icon-views-add"
            onClick={() => props.onCreateViewClick?.()}
            disabled={false}
            tooltipArrow={false}
            connectStyle
          />
          <SortDropDownSelect
            options={sortOptions}
            sortBy={sortBy()}
            sortDirection={sortDirection()}
            onSortChanged={handleSortChanged}
          />
          <UvIconButton
            dataTestId="views-search-button"
            type="action"
            tooltip={t('general.search')}
            icon="tc-icon-search"
            onClick={() => setShowSearch(true)}
            disabled={false}
            tooltipArrow={false}
            connectStyle
          />
        </>
      }
      toggleContent={
        <div class="flex items-center justify-between">
          <i class="icon-font tc-icon-search" />
          <CustomInput
            name="views-search"
            label=""
            class="w-full px-[0.5rem]"
            placeholder={t('general.search')}
            onInputRef={(ref) => {
              ref?.focus();
            }}
            onInput={(
              e: InputEvent & {
                currentTarget: HTMLInputElement;
                target: Element;
              }
            ) => setFilterText(e.currentTarget.value)}
          />
          <UvIconButton
            dataTestId="views-search-close-button"
            type="action"
            tooltip={t('general.close')}
            icon="tc-icon-content-close"
            onClick={clearAndCloseSearch}
            disabled={false}
            tooltipArrow={false}
            connectStyle
          />
        </div>
      }
      showToggleContent={showSearch()}
      content={
        <div>
          <Show when={!currentProjectId()}>
            <div class="p-4 text-center text-gray-600 w-full">
              {t('mobileMapping.views.selectProject')}
            </div>
          </Show>
          <Show when={isLoading()}>
            <div class="w-full h-[300px]">
              <Spinner show={true} />
            </div>
          </Show>
          <Show when={hasErrors()}>
            <Alert type="error">{t('general.unhandledError')}</Alert>
          </Show>
          <Show when={arePrerequisitesMet()}>
            <Switch>
              <Match when={views().length === 0}>
                <div class="p-4 text-center text-gray-600 w-full">
                  {t('mobileMapping.views.noViewsFound')}
                </div>
              </Match>
              <Match when={views().length > 0 && currentProjectId()}>
                <ViewCards
                  views={views()}
                  viewsProvider={props.viewsProvider ?? undefined}
                  tcTcpsApi={props.tcTcpsApi}
                  region={currentRegion()!}
                  projectId={currentProjectId()!}
                  onCardClick={props.onCardClick}
                  onViewDetailsSelect={handleViewDetailsSelect}
                  deletePointCloudAssociationsForView={
                    props.deletePointCloudAssociationsForView
                  }
                  deleteReservedViewData={props.deleteReservedViewData}
                  onViewDeleted={props.onViewDeleted}
                  onViewDeleteError={props.onViewDeleteError}
                  onLoadOriginalVersions={props.onLoadOriginalVersions}
                  onUpdateView={props.onUpdateView}
                />
              </Match>
            </Switch>
          </Show>
        </div>
      }
    />
  );
};
